import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { MessageBubble } from "./MessageBubble";
import { SuggestedPrompts } from "./SuggestedPrompts";
import { useChatbot } from "./useChatbot";
import {
  createChatbotStarredImage,
  deleteChatbotStarredImage,
  getChatbotLineDeliveryStatus,
  getChatbotStarredImages,
  getChatbotUsageStatus,
  sendChatbotImageToLine,
  type ChatbotLineDeliveryStatus,
  type ChatbotStarredImage,
  type ChatbotUsageStatus,
} from "../../services/chatbot.service";
import { ReferenceLibrary } from "./ReferenceLibrary";
import { StarredGallery } from "./StarredGallery";
import { extractMarkdownImageSources } from "./markdown";
import type { WidgetMessage, WidgetToolCall } from "./types";
import "./styles/chatbot.css";
import RestaurantChatbotShell from "../../shared/components/RestaurantChatbotShell";

const SCROLL_BEHAVIOR_SMOOTH = "smooth";
const BUTTON_TYPE = "button";
const SUBMIT_BUTTON_TYPE = "submit";
const TEXTAREA_ROWS = 2;
const TEXTAREA_MAX_LINES = 10;
const OPEN_CLASS_NAME = " open";
const SELECTED_CLASS_NAME = " selected";
const BACK_ICON_CLASS_NAME = "ti ti-arrow-left";
const CLEAR_ICON_CLASS_NAME = "ti ti-edit";
const HISTORY_ICON_CLASS_NAME = "ti ti-history";
const UPLOAD_ICON_CLASS_NAME = "ti ti-photo-plus";
const SEND_ICON_CLASS_NAME = "ti ti-send";
const CLOSE_ICON_CLASS_NAME = "ti ti-x";
const SCROLL_DOWN_ICON_CLASS_NAME = "ti ti-arrow-down";
const COPY_ICON_CLASS_NAME = "ti ti-copy";
const DOWNLOAD_ICON_CLASS_NAME = "ti ti-download";
const DELETE_ICON_CLASS_NAME = "ti ti-trash";
const OPEN_EXTERNAL_ICON_CLASS_NAME = "ti ti-external-link";
const SUCCESS_ICON_CLASS_NAME = "ti ti-check";
const OPEN_ICON_CLASS_NAME = "ti ti-message-chatbot";
const ASSISTANT_ICON_CLASS_NAME = "ti ti-sparkles";
const STAR_ICON_CLASS_NAME = "ti ti-star";
const HEADER_ACTION_BUTTON_CLASS_NAME = "restaurant-chatbot-header-action";
const COMPOSER_ACTION_BUTTON_CLASS_NAME = "restaurant-chatbot-composer-action";
const PAGE_TITLE_TRANSLATION_KEY = "chatbot_page_title";
const PAGE_SUBTITLE_TRANSLATION_KEY = "chatbot_page_subtitle";
const CLEAR_CHAT_TRANSLATION_KEY = "chatbot_clear_chat";
const CHAT_SESSIONS_TRANSLATION_KEY = "chatbot_sessions";
const CHAT_SESSIONS_EMPTY_TRANSLATION_KEY = "chatbot_sessions_empty";
const CHAT_SESSIONS_LOADING_TRANSLATION_KEY = "chatbot_sessions_loading";
const CHAT_SESSION_CURRENT_TRANSLATION_KEY = "chatbot_session_current";
const BACK_TO_CHAT_TRANSLATION_KEY = "chatbot_back_to_chat";
const CLOSE_CHAT_TRANSLATION_KEY = "chatbot_close";
const OPEN_CHAT_TRANSLATION_KEY = "chatbot_open";
const TEASER_MESSAGE_TRANSLATION_KEY = "chatbot_teaser_message";
const DISMISS_TEASER_TRANSLATION_KEY = "chatbot_dismiss_teaser";
const TEASER_DISMISSED_STORAGE_KEY = "wilreji-chatbot-teaser-dismissed";
const TABLET_CHATBOT_MEDIA_QUERY = "(hover: none) and (pointer: coarse) and (max-width: 1400px)";
const getIsCompactViewport = (): boolean => (
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(TABLET_CHATBOT_MEDIA_QUERY).matches
);
const LINE_APP_ICON_SRC = "/assets/common/images/line-app-icon.png";
const INPUT_PLACEHOLDER_TRANSLATION_KEY = "chatbot_input_placeholder";
const SEND_MESSAGE_TRANSLATION_KEY = "chatbot_send_message";
const ATTACH_REFERENCE_IMAGE_TRANSLATION_KEY = "chatbot_attach_reference_image";
const REMOVE_REFERENCE_IMAGE_TRANSLATION_KEY = "chatbot_remove_reference_image";
const UPLOADING_REFERENCE_IMAGE_TRANSLATION_KEY = "chatbot_uploading_reference_image";
const ACCESS_DISABLED_MESSAGE_TRANSLATION_KEY = "chatbot_access_disabled_message";
const ACCESS_CHECKING_TRANSLATION_KEY = "chatbot_access_checking";
const REFERENCE_IMAGE_ACCEPT = "image/png,image/jpeg,image/webp";
const DEFAULT_IMAGE_FILENAME = "generated-image";
const SUGGESTED_PROMPT_TRANSLATION_KEYS = [
  "chatbot_suggested_prompt_1",
  "chatbot_suggested_prompt_2",
  "chatbot_suggested_prompt_3",
  "chatbot_suggested_prompt_4",
];
const STREAM_STATUS_TRANSLATION_KEYS = {
  thinking: "chatbot_thinking",
  generating_image: "chatbot_generating_image",
  editing_image: "chatbot_editing_image",
} as const;
const MARKETING_IMAGE_TOOL_TYPES = new Set(["image_generation", "image_edit"]);

const RELATIVE_TIME_JUST_NOW_KEY = "chatbot_time_just_now";
const RELATIVE_TIME_MINUTES_AGO_KEY = "chatbot_time_minutes_ago";
const RELATIVE_TIME_HOURS_AGO_KEY = "chatbot_time_hours_ago";
const RELATIVE_TIME_DAYS_AGO_KEY = "chatbot_time_days_ago";
const RELATIVE_TIME_WEEKS_AGO_KEY = "chatbot_time_weeks_ago";
const RELATIVE_TIME_MONTHS_AGO_KEY = "chatbot_time_months_ago";
const RELATIVE_TIME_YEARS_AGO_KEY = "chatbot_time_years_ago";

const buildStarredImageKey = (sessionId: string, turnId: string, imageUrl: string): string =>
  `${sessionId}::${turnId}::${imageUrl}`;

const formatRelativeTime = (
  value: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return t(RELATIVE_TIME_JUST_NOW_KEY);

  const abs = Math.abs(Math.floor((timestamp - Date.now()) / 1000));
  if (abs < 60) return t(RELATIVE_TIME_JUST_NOW_KEY);
  if (abs < 3600) return t(RELATIVE_TIME_MINUTES_AGO_KEY, { count: Math.floor(abs / 60) });
  if (abs < 86400) return t(RELATIVE_TIME_HOURS_AGO_KEY, { count: Math.floor(abs / 3600) });
  if (abs < 604800) return t(RELATIVE_TIME_DAYS_AGO_KEY, { count: Math.floor(abs / 86400) });
  if (abs < 2592000) return t(RELATIVE_TIME_WEEKS_AGO_KEY, { count: Math.floor(abs / 604800) });
  if (abs < 31536000) return t(RELATIVE_TIME_MONTHS_AGO_KEY, { count: Math.floor(abs / 2592000) });
  return t(RELATIVE_TIME_YEARS_AGO_KEY, { count: Math.floor(abs / 31536000) });
};

const getImageExtension = (src: string, mimeType?: string): string => {
  const mimeExtension = mimeType?.split("/")[1]?.replace("jpeg", "jpg");
  if (mimeExtension && /^[a-z0-9]+$/i.test(mimeExtension)) return mimeExtension;

  try {
    const pathname = new URL(src, window.location.origin).pathname;
    const extension = pathname.match(/\.([a-z0-9]{2,5})$/i)?.[1];
    return extension?.toLowerCase() ?? "png";
  } catch {
    return "png";
  }
};

const buildImageFilename = (name: string | undefined, src: string, mimeType?: string): string => {
  const extension = getImageExtension(src, mimeType);
  const nameWithoutExtension = (name || DEFAULT_IMAGE_FILENAME).replace(/\.[a-z0-9]{2,5}$/i, "");
  const slug = nameWithoutExtension
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

  return `${slug || DEFAULT_IMAGE_FILENAME}.${extension}`;
};

const RestaurantChatbotWidget = (): React.ReactElement => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCompactViewport, setIsCompactViewport] = useState(getIsCompactViewport);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [isAccessDisabled, setIsAccessDisabled] = useState(false);
  const [openedImage, setOpenedImage] = useState<{
    src: string;
    alt: string;
    filename: string;
  } | null>(null);
  const [openedImageContextOverride, setOpenedImageContextOverride] = useState<{
    sessionId: string;
    turnId: string;
    title: string;
    starredId?: string;
  } | null>(null);
  const [viewerActionFeedback, setViewerActionFeedback] = useState<
    "copy" | "download" | "open" | "star" | null
  >(null);
  const viewerFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showTeaser, setShowTeaser] = useState(() => {
    try {
      return sessionStorage.getItem(TEASER_DISMISSED_STORAGE_KEY) !== "true";
    } catch {
      return true;
    }
  });
  const [usageStatus, setUsageStatus] = useState<ChatbotUsageStatus | null>(null);
  const [lineDeliveryStatus, setLineDeliveryStatus] = useState<ChatbotLineDeliveryStatus | null>(null);
  const [isLoadingUsageStatus, setIsLoadingUsageStatus] = useState(false);
  const [isUsageOpen, setIsUsageOpen] = useState(false);
  const [isReferencesOpen, setIsReferencesOpen] = useState(false);
  const [isStarredOpen, setIsStarredOpen] = useState(false);
  const [starredImages, setStarredImages] = useState<ChatbotStarredImage[]>([]);
  const [isLoadingStarredImages, setIsLoadingStarredImages] = useState(false);
  const [highlightedTurnId, setHighlightedTurnId] = useState<string | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const widgetRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const referenceImageInputRef = useRef<HTMLInputElement>(null);
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const {
    messages,
    input,
    setInput,
    isStreaming,
    streamStatus,
    isLoadingSessions,
    isLoadingSessionTurns,
    isUploadingReferenceImages,
    isSessionListOpen,
    sessions,
    referenceImages,
    referenceImageUploadError,
    activeSessionId,
    copiedId,
    welcomeName,
    hasUserMessages,
    messagesEndRef,
    handleSend,
    handleCopy,
    handleSuggestedPrompt,
    handleOpenSessions,
    handleToggleSessions,
    handleSelectSession,
    handleDeleteSession,
    handleReferenceImagesSelected,
    handleRemoveReferenceImage,
    handleReset,
  } = useChatbot();

  useLayoutEffect(() => {
    const textarea = composerInputRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const styles = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(styles.lineHeight);
    const fontSize = Number.parseFloat(styles.fontSize) || 16;
    const resolvedLineHeight = Number.isFinite(lineHeight) ? lineHeight : fontSize * 1.4;
    const verticalPadding = (Number.parseFloat(styles.paddingTop) || 0) + (Number.parseFloat(styles.paddingBottom) || 0);
    const maxHeight = resolvedLineHeight * TEXTAREA_MAX_LINES + verticalPadding;

    textarea.style.maxHeight = `${maxHeight}px`;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [input, isCompactViewport, isStarredOpen, isReferencesOpen, isUsageOpen, isSessionListOpen]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia(TABLET_CHATBOT_MEDIA_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent): void => {
      setIsCompactViewport(event.matches);
    };

    setIsCompactViewport(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleViewportChange);
    } else {
      mediaQuery.addListener?.(handleViewportChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleViewportChange);
      } else {
        mediaQuery.removeListener?.(handleViewportChange);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCompactViewport || !isOpen) return undefined;

    const root = document.documentElement;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverscrollBehavior = root.style.overscrollBehavior;
    const previousBodyOverscrollBehavior = document.body.style.overscrollBehavior;
    root.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    document.body.style.overscrollBehavior = "none";

    return () => {
      root.style.overflow = previousRootOverflow;
      document.body.style.overflow = previousBodyOverflow;
      root.style.overscrollBehavior = previousRootOverscrollBehavior;
      document.body.style.overscrollBehavior = previousBodyOverscrollBehavior;
    };
  }, [isCompactViewport, isOpen]);

  useEffect(() => {
    if (isOpen && isAtBottom) messagesEndRef.current?.scrollIntoView({ behavior: SCROLL_BEHAVIOR_SMOOTH });
  }, [messages, isOpen, messagesEndRef, isAtBottom]);

  useEffect(() => {
    if (!highlightedTurnId) return;
    const element = document.querySelector(
      `.restaurant-chatbot-message-row.assistant[data-turn-id="${highlightedTurnId}"]`
    );
    if (!element) return;
    element.scrollIntoView({ behavior: SCROLL_BEHAVIOR_SMOOTH, block: "center" });
    const timer = window.setTimeout(() => setHighlightedTurnId(null), 2400);
    return () => window.clearTimeout(timer);
  }, [highlightedTurnId, messages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: SCROLL_BEHAVIOR_SMOOTH });
  }, [messagesEndRef]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setIsAtBottom(scrollHeight - scrollTop - clientHeight < 40);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchUsageStatus = () => {
    if (isLoadingUsageStatus) return;
    setIsLoadingUsageStatus(true);
    void getChatbotUsageStatus()
      .then((data) => setUsageStatus(data))
      .catch(() => setUsageStatus(null))
      .finally(() => setIsLoadingUsageStatus(false));
  };

  const loadStarredImages = useCallback(() => {
    setIsLoadingStarredImages(true);
    void getChatbotStarredImages()
      .then((data) => setStarredImages(data))
      .catch(() => setStarredImages([]))
      .finally(() => setIsLoadingStarredImages(false));
  }, []);

  const isStreamingRef = useRef(isStreaming);
  useEffect(() => {
    if (isStreamingRef.current && !isStreaming && (isUsageOpen || usageStatus)) {
      fetchUsageStatus();
    }
    isStreamingRef.current = isStreaming;
  }, [isStreaming, isUsageOpen, usageStatus]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest(".swal2-container")) {
        return;
      }
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const suggestedPrompts = SUGGESTED_PROMPT_TRANSLATION_KEYS.map((key) => t(key));
  const streamingStatusTranslationKey = STREAM_STATUS_TRANSLATION_KEYS[streamStatus];
  const isComposerDisabled = isStreaming || isUploadingReferenceImages || isAccessDisabled;
  const isSendDisabled = !input.trim() || isComposerDisabled;
  const isDetailViewOpen = isStarredOpen || isReferencesOpen || isUsageOpen || isSessionListOpen;

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    setIsCheckingAvailability(true);

    void Promise.allSettled([
      getChatbotUsageStatus(),
      getChatbotLineDeliveryStatus(),
    ])
      .then(([usageResult, lineResult]) => {
        if (!isMounted) return;

        if (usageResult.status === "fulfilled") {
          setUsageStatus(usageResult.value);
          setIsAccessDisabled(false);
        } else {
          const error = usageResult.reason as { status?: number };
          if (error?.status === 403) {
            setIsAccessDisabled(true);
          } else {
            setIsAccessDisabled(false);
          }
        }

        if (lineResult.status === "fulfilled") {
          setLineDeliveryStatus(lineResult.value);
        } else {
          setLineDeliveryStatus(null);
        }
      })
      .finally(() => {
        if (isMounted) setIsCheckingAvailability(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const handleReferenceImageInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    handleReferenceImagesSelected(event.target.files);
    event.target.value = "";
  };
  const dismissTeaser = (): void => {
    setShowTeaser(false);
    try {
      sessionStorage.setItem(TEASER_DISMISSED_STORAGE_KEY, "true");
    } catch {
      // In-memory state still prevents repeat display when storage is unavailable.
    }
  };
  const openImageViewer = (src: string, alt: string, filename?: string): void => {
    setOpenedImageContextOverride(null);
    setOpenedImage({ src, alt, filename: buildImageFilename(filename || alt, src) });
  };

  const openStarredImageViewer = (item: ChatbotStarredImage): void => {
    setOpenedImage({
      src: item.image_url,
      alt: item.title,
      filename: buildImageFilename(item.title || DEFAULT_IMAGE_FILENAME, item.image_url),
    });
    setOpenedImageContextOverride({
      sessionId: item.session_id,
      turnId: item.turn_id,
      title: item.title,
      starredId: item.id,
    });
  };

  const closeImageViewer = (): void => {
    setOpenedImage(null);
    setOpenedImageContextOverride(null);
    setViewerActionFeedback(null);
  };

  const showViewerActionFeedback = (action: "copy" | "download" | "open" | "star"): void => {
    setViewerActionFeedback(action);
    if (viewerFeedbackTimerRef.current) clearTimeout(viewerFeedbackTimerRef.current);
    viewerFeedbackTimerRef.current = setTimeout(() => setViewerActionFeedback(null), 1600);
  };

  const copyImageToClipboard = async (src: string): Promise<void> => {
    try {
      const response = await fetch(src, { mode: "cors" });
      if (!response.ok) throw new Error("Image download failed");
      const blob = await response.blob();
      let pngBlob = blob;
      if (blob.type !== "image/png") {
        pngBlob = await new Promise<Blob>((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.drawImage(img, 0, 0);
              canvas.toBlob((result) => {
                if (result) resolve(result);
                else reject(new Error("Canvas conversion to PNG failed"));
              }, "image/png");
            } else {
              reject(new Error("Canvas context not available"));
            }
          };
          img.onerror = () => reject(new Error("Image failed to load for conversion"));
          img.src = src;
        });
      }
      await navigator.clipboard.write([
        new ClipboardItem({
          [pngBlob.type]: pngBlob,
        }),
      ]);
    } catch (err) {
      console.error("Failed to copy image to clipboard, copying URL as fallback:", err);
      await navigator.clipboard.writeText(src);
    }
  };

  const handleDownloadImage = async (src: string, filename = "image"): Promise<boolean> => {
    try {
      const response = await fetch(src, { mode: "cors" });
      if (!response.ok || !response.headers.get("content-type")?.startsWith("image/")) {
        throw new Error("Image is unavailable");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildImageFilename(filename, src, blob.type);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return true;
    } catch {
      // Do not open failed image responses, which may expose an XML/HTML storage error page.
      return false;
    }
  };

  const handleToggleChat = (): void => {
    if (!isOpen) dismissTeaser();
    setIsOpen((value) => !value);
  };

  const handleViewerBackdropClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      closeImageViewer();
    }
  };

  const handleViewerClose = (): void => {
    closeImageViewer();
  };

  const handleOpenImageInNewWindow = (src: string): void => {
    window.open(src, "_blank", "noopener,noreferrer");
    showViewerActionFeedback("open");
  };

  const isLineDeliveryAvailable = Boolean(lineDeliveryStatus?.available);

  const starredImagesByKey = useMemo(
    () =>
      new Map(
        starredImages.map((item) => [
          buildStarredImageKey(item.session_id, item.turn_id, item.image_url),
          item,
        ])
      ),
    [starredImages]
  );

  const messageImageContextsByUrl = useMemo(() => {
    const map = new Map<string, { sessionId: string; turnId: string; title: string }>();
    messages.forEach((message) => {
      const hasMarketingImageTool = (message.toolCalls ?? []).some((toolCall: WidgetToolCall) =>
        MARKETING_IMAGE_TOOL_TYPES.has(toolCall.tool_type || "")
      );
      if (
        message.role !== "assistant" ||
        !message.sessionId ||
        !message.turnId ||
        !hasMarketingImageTool ||
        !message.content
      ) {
        return;
      }

      extractMarkdownImageSources(message.content).forEach((image) => {
        map.set(image.src, {
          sessionId: message.sessionId!,
          turnId: message.turnId!,
          title: image.alt || "",
        });
      });
    });
    return map;
  }, [messages]);

  const toggleStarredImage = useCallback(async ({
    sessionId,
    turnId,
    imageUrl,
    title,
  }: {
    sessionId: string;
    turnId: string;
    imageUrl: string;
    title?: string;
  }): Promise<void> => {
    const existing = starredImagesByKey.get(buildStarredImageKey(sessionId, turnId, imageUrl));
    if (existing) {
      await deleteChatbotStarredImage(existing.id);
      setStarredImages((current) => current.filter((item) => item.id !== existing.id));
      return;
    }

    const created = await createChatbotStarredImage({
      session_id: sessionId,
      turn_id: turnId,
      image_url: imageUrl,
      ...(title ? { title } : {}),
    });
    setStarredImages((current) => {
      const next = current.filter((item) => item.id !== created.id);
      return [created, ...next];
    });
  }, [starredImagesByKey]);

  const getImageStarStateForMessage = useCallback((message: WidgetMessage, src: string, alt: string) => {
    const hasMarketingImageTool = (message.toolCalls ?? []).some((toolCall: WidgetToolCall) =>
      MARKETING_IMAGE_TOOL_TYPES.has(toolCall.tool_type || "")
    );
    const sessionId = message.sessionId;
    const turnId = message.turnId;
    const canStar = message.role === "assistant" && !!sessionId && !!turnId && hasMarketingImageTool;
    const isStarred =
      canStar && sessionId && turnId
        ? starredImagesByKey.has(buildStarredImageKey(sessionId, turnId, src))
        : false;
    const onToggleStar =
      canStar && sessionId && turnId
        ? () => toggleStarredImage({
          sessionId,
          turnId,
          imageUrl: src,
          title: alt,
        })
        : undefined;

    return {
      canStar,
      isStarred,
      onToggleStar,
    };
  }, [starredImagesByKey, toggleStarredImage]);

  const sendImageToLineWithConfirmation = useCallback(async ({
    sessionId,
    turnId,
    imageUrl,
    title,
  }: {
    sessionId: string;
    turnId: string;
    imageUrl: string;
    title?: string;
  }): Promise<void> => {
    const confirmation = await Swal.fire({
      title: t("chatbot_send_to_line", { defaultValue: "Send to LINE" }),
      text: t("chatbot_send_to_line_confirm", {
        defaultValue: "This will send the selected creative to your restaurant LINE audience.",
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("chatbot_send_to_line", { defaultValue: "Send to LINE" }),
      cancelButtonText: t("btn_cancel", { defaultValue: "Cancel" }),
    });

    if (!confirmation.isConfirmed) return;

    Swal.fire({
      title: t("chatbot_send_to_line_sending", { defaultValue: "Sending to LINE..." }),
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await sendChatbotImageToLine({
        image_url: imageUrl,
        session_id: sessionId,
        turn_id: turnId,
        ...(title ? { title } : {}),
      });

      await Swal.fire({
        icon: "success",
        title: t("chatbot_send_to_line_success", { defaultValue: "Sent to LINE" }),
        text: t("chatbot_send_to_line_success_message", {
          defaultValue: "The selected creative was sent to your LINE audience.",
        }),
      });
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : t("chatbot_send_to_line_error", { defaultValue: "LINE send failed. Please try again." });

      await Swal.fire({
        icon: "error",
        title: t("chatbot_send_to_line_error_title", { defaultValue: "LINE send failed" }),
        text: message,
      });
    }
  }, [t]);

  const getImageLineStateForMessage = useCallback((message: WidgetMessage, src: string, alt: string) => {
    const hasMarketingImageTool = (message.toolCalls ?? []).some((toolCall: WidgetToolCall) =>
      MARKETING_IMAGE_TOOL_TYPES.has(toolCall.tool_type || "")
    );
    const sessionId = message.sessionId;
    const turnId = message.turnId;
    const canSend = Boolean(
      isLineDeliveryAvailable
      && message.role === "assistant"
      && sessionId
      && turnId
      && hasMarketingImageTool
    );
    const onSend = canSend && sessionId && turnId
      ? () => sendImageToLineWithConfirmation({
        sessionId,
        turnId,
        imageUrl: src,
        title: alt,
      })
      : undefined;

    return {
      canSend,
      onSend,
    };
  }, [isLineDeliveryAvailable, sendImageToLineWithConfirmation]);

  const viewerImageContext = useMemo(() => {
    if (!openedImage) return null;
    if (openedImageContextOverride) return openedImageContextOverride;
    const messageContext = messageImageContextsByUrl.get(openedImage.src);
    if (messageContext) return messageContext;
    return null;
  }, [messageImageContextsByUrl, openedImage, openedImageContextOverride]);

  const viewerStarredImage = useMemo(() => {
    if (!openedImage || !viewerImageContext) return undefined;
    return starredImagesByKey.get(
      buildStarredImageKey(viewerImageContext.sessionId, viewerImageContext.turnId, openedImage.src)
    );
  }, [openedImage, starredImagesByKey, viewerImageContext]);

  const handleJumpToContext = (item: ChatbotStarredImage): void => {
    setIsStarredOpen(false);
    setIsReferencesOpen(false);
    setIsUsageOpen(false);
    closeImageViewer();
    setHighlightedTurnId(item.turn_id);
    void handleSelectSession(item.session_id);
  };

  const closeChatLabel = t(isCompactViewport ? "chatbot_back_to_dashboard" : CLOSE_CHAT_TRANSLATION_KEY, {
    defaultValue: isCompactViewport ? "Back" : "Close chat",
  });

  return (
    <RestaurantChatbotShell compact={isCompactViewport} open={isOpen} widgetRef={widgetRef}>
      <section
        className={`restaurant-chatbot-panel${isOpen ? OPEN_CLASS_NAME : ""}`}
        role="region"
        aria-label={t(PAGE_TITLE_TRANSLATION_KEY)}
        aria-hidden={!isOpen}
      >
          <div className={`restaurant-chatbot-panel-header${isDetailViewOpen ? " is-detail-view" : ""}`}>
            <div className="restaurant-chatbot-panel-header-main">
              <div className="restaurant-chatbot-panel-identity">
                <span className="restaurant-chatbot-header-avatar" aria-hidden="true">
                  <i className={ASSISTANT_ICON_CLASS_NAME} />
                </span>
                <div>
                  <h4 className="restaurant-chatbot-header-title">
                    {t(PAGE_TITLE_TRANSLATION_KEY)}
                  </h4>
                  <p className="restaurant-chatbot-header-subtitle">
                    {t(PAGE_SUBTITLE_TRANSLATION_KEY)}
                  </p>
                </div>
              </div>
              <button
                className={`restaurant-chatbot-header-close${isCompactViewport ? " is-dashboard-close" : ""}`}
                type={BUTTON_TYPE}
                onClick={() => setIsOpen(false)}
                title={closeChatLabel}
                aria-label={closeChatLabel}
              >
                <i className={CLOSE_ICON_CLASS_NAME} />
                {isCompactViewport && <span>{closeChatLabel}</span>}
              </button>
            </div>
            {!isDetailViewOpen ? (
              <div className="restaurant-chatbot-panel-actions" role="toolbar" aria-label="Chatbot actions">
                <button
                  className={`btn btn-icon btn-outline-primary ${HEADER_ACTION_BUTTON_CLASS_NAME}${isStarredOpen ? SELECTED_CLASS_NAME : ""}`}
                  type={BUTTON_TYPE}
                  onClick={() => {
                    if (!isStarredOpen) {
                      if (isSessionListOpen) handleToggleSessions();
                      setIsReferencesOpen(false);
                      setIsUsageOpen(false);
                      setIsStarredOpen(true);
                      loadStarredImages();
                    } else {
                      setIsStarredOpen(false);
                    }
                  }}
                  title={t("chatbot_starred", { defaultValue: "Starred" })}
                  disabled={isStreaming}
                >
                  <i className={STAR_ICON_CLASS_NAME} />
                  <span>{t("chatbot_starred", { defaultValue: "Starred" })}</span>
                </button>
                <button
                  className={`btn btn-icon btn-outline-primary ${HEADER_ACTION_BUTTON_CLASS_NAME}${isReferencesOpen ? SELECTED_CLASS_NAME : ""}`}
                  type={BUTTON_TYPE}
                  onClick={() => {
                    if (!isReferencesOpen) {
                      if (isSessionListOpen) handleToggleSessions();
                      setIsUsageOpen(false);
                      setIsStarredOpen(false);
                      setIsReferencesOpen(true);
                    } else {
                      setIsReferencesOpen(false);
                    }
                  }}
                  title={t("chatbot_references")}
                  disabled={isStreaming}
                >
                  <i className="ti ti-photo" />
                  <span>{t("chatbot_references")}</span>
                </button>
                <button
                  className={`btn btn-icon btn-outline-primary ${HEADER_ACTION_BUTTON_CLASS_NAME}${isUsageOpen ? SELECTED_CLASS_NAME : ""}`}
                  type={BUTTON_TYPE}
                  onClick={() => {
                    if (!isUsageOpen) {
                      if (isSessionListOpen) handleToggleSessions();
                      setIsReferencesOpen(false);
                      setIsStarredOpen(false);
                      setIsUsageOpen(true);
                      if (!usageStatus && !isCheckingAvailability) fetchUsageStatus();
                    } else {
                      setIsUsageOpen(false);
                    }
                  }}
                  title={t("chatbot_usage_details_title")}
                  disabled={isStreaming}
                >
                  <i className="ti ti-chart-bar" />
                  <span>{t("chatbot_usage")}</span>
                </button>
                <button
                  className={`btn btn-icon btn-outline-primary ${HEADER_ACTION_BUTTON_CLASS_NAME}${isSessionListOpen ? SELECTED_CLASS_NAME : ""}`}
                  type={BUTTON_TYPE}
                  onClick={() => {
                    setIsReferencesOpen(false);
                    setIsUsageOpen(false);
                    setIsStarredOpen(false);
                    handleOpenSessions();
                  }}
                  title={t(CHAT_SESSIONS_TRANSLATION_KEY)}
                  disabled={isStreaming}
                >
                  <i className={HISTORY_ICON_CLASS_NAME} />
                  <span>{t(CHAT_SESSIONS_TRANSLATION_KEY)}</span>
                </button>
                <button
                  className={`btn btn-icon btn-outline-primary ${HEADER_ACTION_BUTTON_CLASS_NAME}`}
                  type={BUTTON_TYPE}
                  onClick={() => {
                    setIsReferencesOpen(false);
                    setIsUsageOpen(false);
                    setIsStarredOpen(false);
                    handleReset();
                  }}
                  title={t(CLEAR_CHAT_TRANSLATION_KEY)}
                >
                  <i className={CLEAR_ICON_CLASS_NAME} />
                  <span>{t("chatbot_new_chat")}</span>
                </button>
              </div>
            ) : null}
          </div>

          {isStarredOpen ? (
            <StarredGallery
              items={starredImages}
              isLoading={isLoadingStarredImages}
              showSendToLine={isLineDeliveryAvailable}
              onBack={() => setIsStarredOpen(false)}
              onOpen={openStarredImageViewer}
              onDownload={(item) => {
                void handleDownloadImage(item.image_url, item.title || "image");
              }}
              onSendToLine={(item) => {
                void sendImageToLineWithConfirmation({
                  sessionId: item.session_id,
                  turnId: item.turn_id,
                  imageUrl: item.image_url,
                  title: item.title,
                });
              }}
              onJumpToContext={handleJumpToContext}
              onUnstar={(item) => {
                void deleteChatbotStarredImage(item.id).then(() => {
                  setStarredImages((current) => current.filter((currentItem) => currentItem.id !== item.id));
                });
              }}
            />
          ) : isReferencesOpen ? (
            <ReferenceLibrary onBack={() => setIsReferencesOpen(false)} />
          ) : isUsageOpen ? (
            <div className="restaurant-chatbot-usage-view">
              <div className="restaurant-chatbot-session-view-header">
                <button
                  className="btn btn-icon btn-outline-primary"
                  type={BUTTON_TYPE}
                  onClick={() => setIsUsageOpen(false)}
                  title={t(BACK_TO_CHAT_TRANSLATION_KEY)}
                >
                  <i className={BACK_ICON_CLASS_NAME} />
                </button>
                <span>{t("chatbot_usage_details_title")}</span>
              </div>
              <div className="restaurant-chatbot-usage-content">
                {isLoadingUsageStatus ? (
                  <div className="restaurant-chatbot-session-state">{t("chatbot_usage_loading")}</div>
                ) : isCheckingAvailability && !usageStatus ? (
                  <div className="restaurant-chatbot-session-state">
                    {t(ACCESS_CHECKING_TRANSLATION_KEY, { defaultValue: "Checking chatbot availability..." })}
                  </div>
                ) : usageStatus?.usage_windows?.length ? (
                  <div className="restaurant-chatbot-usage-list">
                    {usageStatus.usage_windows.map((window) => {
                      const windowLabel = t(`chatbot_usage_window_${window.key}`, {
                        defaultValue: window.key.charAt(0).toUpperCase() + window.key.slice(1),
                      });
                      const tokenPct = window.token_limit > 0 ? Math.min(100, Math.round((window.tokens_used / window.token_limit) * 100)) : 0;
                      const imagePct = window.image_generation_limit > 0 ? Math.min(100, Math.round((window.image_generations_used / window.image_generation_limit) * 100)) : 0;
                      const costPct = window.cost_limit_usd > 0 ? Math.min(100, Math.round((window.cost_usd / window.cost_limit_usd) * 100)) : 0;

                      return (
                        <div key={window.key} className="restaurant-chatbot-usage-window-card">
                          <h5 className="restaurant-chatbot-usage-window-title">
                            {t("chatbot_usage_window_limits", { window: windowLabel })}
                          </h5>

                          {window.token_limit > 0 && (
                            <div className="restaurant-chatbot-usage-item-detail">
                              <div className="restaurant-chatbot-usage-item-meta">
                                <span>{t("chatbot_usage_tokens")}</span>
                                <span>{window.tokens_used.toLocaleString()} / {window.token_limit.toLocaleString()} ({tokenPct}%)</span>
                              </div>
                              <div className="restaurant-chatbot-usage-progress">
                                <div className="restaurant-chatbot-usage-progress-fill" style={{ width: `${tokenPct}%` }} />
                              </div>
                            </div>
                          )}

                          {window.image_generation_limit > 0 && (
                            <div className="restaurant-chatbot-usage-item-detail">
                              <div className="restaurant-chatbot-usage-item-meta">
                                <span>{t("chatbot_usage_image_generations")}</span>
                                <span>{window.image_generations_used} / {window.image_generation_limit} ({imagePct}%)</span>
                              </div>
                              <div className="restaurant-chatbot-usage-progress">
                                <div className="restaurant-chatbot-usage-progress-fill" style={{ width: `${imagePct}%` }} />
                              </div>
                            </div>
                          )}

                          {window.cost_limit_usd > 0 && (
                            <div className="restaurant-chatbot-usage-item-detail">
                              <div className="restaurant-chatbot-usage-item-meta">
                                <span>{t("chatbot_usage_estimated_cost")}</span>
                                <span>${window.cost_usd.toFixed(4)} / ${window.cost_limit_usd.toFixed(2)} ({costPct}%)</span>
                              </div>
                              <div className="restaurant-chatbot-usage-progress">
                                <div className="restaurant-chatbot-usage-progress-fill" style={{ width: `${costPct}%` }} />
                              </div>
                            </div>
                          )}

                          {window.reset_at && (
                            <div className="restaurant-chatbot-usage-reset-time">
                              {t("chatbot_usage_resets_at", {
                                resetAt: new Date(window.reset_at).toLocaleString(),
                                interpolation: { escapeValue: false },
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="restaurant-chatbot-session-state">{t("chatbot_usage_no_limits")}</div>
                )}
              </div>
            </div>
          ) : isSessionListOpen ? (
            <div className="restaurant-chatbot-session-view">
              <div className="restaurant-chatbot-session-view-header">
                <button
                  className="btn btn-icon btn-outline-primary"
                  type={BUTTON_TYPE}
                  onClick={handleToggleSessions}
                  title={t(BACK_TO_CHAT_TRANSLATION_KEY)}
                >
                  <i className={BACK_ICON_CLASS_NAME} />
                </button>
                <span>{t(CHAT_SESSIONS_TRANSLATION_KEY)}</span>
              </div>
              <div className="restaurant-chatbot-session-list">
                {isLoadingSessions ? (
                  <div className="restaurant-chatbot-session-state">
                    {t(CHAT_SESSIONS_LOADING_TRANSLATION_KEY)}
                  </div>
                ) : sessions.length ? (
                  sessions.map((session) => {
                    const isSelected = activeSessionId === session._id;
                    return (
                      <div
                        className={`restaurant-chatbot-session-item${isSelected ? SELECTED_CLASS_NAME : ""}`}
                        key={session._id}
                      >
                        <button
                          className="restaurant-chatbot-session-main"
                          type={BUTTON_TYPE}
                          onClick={() => {
                            void handleSelectSession(session._id);
                          }}
                          disabled={isLoadingSessionTurns}
                        >
                          <span className="restaurant-chatbot-session-title">
                            {session.title || t(CHAT_SESSIONS_TRANSLATION_KEY)}
                          </span>
                          <span className="restaurant-chatbot-session-meta">
                            {formatRelativeTime(session.last_message_at, t)}
                            {isSelected ? ` · ${t(CHAT_SESSION_CURRENT_TRANSLATION_KEY)}` : ""}
                          </span>
                        </button>
                        <button
                          className="restaurant-chatbot-session-delete"
                          type={BUTTON_TYPE}
                          onClick={() => {
                            void handleDeleteSession(session._id);
                          }}
                          disabled={isLoadingSessionTurns}
                          title={t("chatbot_delete_session", { defaultValue: "Delete session" })}
                          aria-label={t("chatbot_delete_session", { defaultValue: "Delete session" })}
                        >
                          <i className={DELETE_ICON_CLASS_NAME} />
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="restaurant-chatbot-session-state">
                    {t(CHAT_SESSIONS_EMPTY_TRANSLATION_KEY)}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="restaurant-chatbot-messages-wrap">
                <div className="restaurant-chatbot-messages" ref={messagesContainerRef}>
                  {messages.map((message) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      copiedId={copiedId}
                      welcomeName={welcomeName}
                      highlightedTurnId={highlightedTurnId}
                      onCopy={handleCopy}
                      onOpenImageViewer={openImageViewer}
                      onCopyImage={copyImageToClipboard}
                      onDownloadImage={handleDownloadImage}
                      getImageStarState={getImageStarStateForMessage}
                      getImageLineState={getImageLineStateForMessage}
                    />
                  ))}

                  {isStreaming && (
                    <div className="restaurant-chatbot-message-row assistant restaurant-chatbot-stream-row">
                      <div className="restaurant-chatbot-avatar" aria-hidden="true">
                        <i className={ASSISTANT_ICON_CLASS_NAME} />
                      </div>
                      <div className="restaurant-chatbot-stream-status" role="status">
                        <span className="restaurant-chatbot-stream-loader" />
                        <span>{t(streamingStatusTranslationKey)}</span>
                      </div>
                    </div>
                  )}

                  {isCheckingAvailability && (
                    <div className="restaurant-chatbot-session-state">
                      {t(ACCESS_CHECKING_TRANSLATION_KEY, { defaultValue: "Checking chatbot availability..." })}
                    </div>
                  )}

                  {isAccessDisabled && !isCheckingAvailability && (
                    <div className="restaurant-chatbot-session-state">
                      {t(ACCESS_DISABLED_MESSAGE_TRANSLATION_KEY, { defaultValue: "Chatbot access is not enabled for this restaurant. Please contact your administrator to request access." })}
                    </div>
                  )}

                  {!hasUserMessages && !isStreaming && !isAccessDisabled && (
                    <SuggestedPrompts prompts={suggestedPrompts} onSelect={handleSuggestedPrompt} />
                  )}

                  <div ref={messagesEndRef} />
                </div>
                {!isAtBottom && (
                  <button
                    className="restaurant-chatbot-scroll-to-bottom"
                    type={BUTTON_TYPE}
                    onClick={scrollToBottom}
                    title={t("chatbot_scroll_to_bottom")}
                    aria-label={t("chatbot_scroll_to_bottom")}
                  >
                    <i className={SCROLL_DOWN_ICON_CLASS_NAME} />
                  </button>
                )}
              </div>

              <form className="restaurant-chatbot-composer" onSubmit={handleSend}>
                {(referenceImages.length > 0 || isUploadingReferenceImages || referenceImageUploadError) && (
                  <div className="restaurant-chatbot-reference-images">
                    {referenceImages.map((image) => (
                      <div className="restaurant-chatbot-reference-image" key={image.image_url}>
                        <button
                          className="restaurant-chatbot-reference-image-preview-button"
                          type={BUTTON_TYPE}
                          onClick={() => openImageViewer(image.image_url, image.file_name, image.file_name)}
                          title={image.file_name}
                        >
                          <img src={image.image_url} alt={image.file_name} />
                        </button>
                        <button
                          className="restaurant-chatbot-reference-image-remove"
                          type={BUTTON_TYPE}
                          onClick={() => handleRemoveReferenceImage(image.image_url)}
                          title={t(REMOVE_REFERENCE_IMAGE_TRANSLATION_KEY)}
                          disabled={isStreaming}
                        >
                          <i className={CLOSE_ICON_CLASS_NAME} />
                        </button>
                      </div>
                    ))}
                    {isUploadingReferenceImages && (
                      <span className="restaurant-chatbot-reference-image-status">
                        {t(UPLOADING_REFERENCE_IMAGE_TRANSLATION_KEY)}
                      </span>
                    )}
                    {referenceImageUploadError && (
                      <span className="restaurant-chatbot-reference-image-error">
                        {referenceImageUploadError}
                      </span>
                    )}
                  </div>
                )}
                <input
                  ref={referenceImageInputRef}
                  type="file"
                  accept={REFERENCE_IMAGE_ACCEPT}
                  multiple
                  hidden
                  onChange={handleReferenceImageInputChange}
                />
                <textarea
                  ref={composerInputRef}
                  className="restaurant-chatbot-composer-input"
                  rows={TEXTAREA_ROWS}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t(INPUT_PLACEHOLDER_TRANSLATION_KEY)}
                  disabled={isComposerDisabled}
                />
                <button
                  className={`btn btn-outline-primary btn-icon ${COMPOSER_ACTION_BUTTON_CLASS_NAME}`}
                  type={BUTTON_TYPE}
                  onClick={() => referenceImageInputRef.current?.click()}
                  disabled={isComposerDisabled}
                  title={t(ATTACH_REFERENCE_IMAGE_TRANSLATION_KEY)}
                >
                  <i className={UPLOAD_ICON_CLASS_NAME} />
                </button>
                <button
                  className={`btn btn-primary btn-icon ${COMPOSER_ACTION_BUTTON_CLASS_NAME}`}
                  type={SUBMIT_BUTTON_TYPE}
                  disabled={isSendDisabled}
                  title={t(SEND_MESSAGE_TRANSLATION_KEY)}
                >
                  <i className={SEND_ICON_CLASS_NAME} />
                </button>
              </form>
            </>
          )}
      </section>

      {openedImage && (
        <div className="restaurant-chatbot-image-viewer-backdrop" onClick={handleViewerBackdropClick}>
          <div className="restaurant-chatbot-image-viewer" role="dialog" aria-modal="true">
            <div className="restaurant-chatbot-image-viewer-actions">
              <button
                className={`restaurant-chatbot-image-viewer-action${
                  viewerActionFeedback === "copy" ? " success" : ""
                }`}
                type={BUTTON_TYPE}
                onClick={() => {
                  void copyImageToClipboard(openedImage.src).then(() =>
                    showViewerActionFeedback("copy")
                  );
                }}
                aria-label="Copy image"
                data-tooltip={viewerActionFeedback === "copy" ? "Copied!" : "Copy image"}
              >
                <i
                  className={
                    viewerActionFeedback === "copy" ? SUCCESS_ICON_CLASS_NAME : COPY_ICON_CLASS_NAME
                  }
                />
              </button>
              <button
                className={`restaurant-chatbot-image-viewer-action${
                  viewerActionFeedback === "download" ? " success" : ""
                }`}
                type={BUTTON_TYPE}
                onClick={() => {
                  void handleDownloadImage(openedImage.src, openedImage.filename).then((downloaded) => {
                    if (downloaded) showViewerActionFeedback("download");
                  });
                }}
                aria-label="Download image"
                data-tooltip={
                  viewerActionFeedback === "download" ? "Downloaded!" : "Download image"
                }
              >
                <i
                  className={
                    viewerActionFeedback === "download"
                      ? SUCCESS_ICON_CLASS_NAME
                      : DOWNLOAD_ICON_CLASS_NAME
                  }
                />
              </button>
              <button
                className={`restaurant-chatbot-image-viewer-action${
                  viewerActionFeedback === "open" ? " success" : ""
                }`}
                type={BUTTON_TYPE}
                onClick={() => handleOpenImageInNewWindow(openedImage.src)}
                aria-label="Open image in new window"
                data-tooltip={
                  viewerActionFeedback === "open" ? "Opened!" : "Open image in new window"
                }
              >
                <i
                  className={
                    viewerActionFeedback === "open"
                      ? SUCCESS_ICON_CLASS_NAME
                      : OPEN_EXTERNAL_ICON_CLASS_NAME
                  }
                />
              </button>
              {viewerImageContext ? (
                <button
                  className={`restaurant-chatbot-image-viewer-action${
                    viewerActionFeedback === "star" ? " success" : ""
                  }${viewerStarredImage ? " active" : ""}`}
                  type={BUTTON_TYPE}
                  onClick={() => {
                    if (!openedImage) return;
                    void toggleStarredImage({
                      sessionId: viewerImageContext.sessionId,
                      turnId: viewerImageContext.turnId,
                      imageUrl: openedImage.src,
                      title: viewerImageContext.title || openedImage.alt,
                    }).then(() => showViewerActionFeedback("star"));
                  }}
                  aria-label={viewerStarredImage
                    ? t("chatbot_unstar_image", { defaultValue: "Remove from starred" })
                    : t("chatbot_star_image", { defaultValue: "Star image" })}
                  data-tooltip={viewerStarredImage
                    ? t("chatbot_unstar_image", { defaultValue: "Remove from starred" })
                    : t("chatbot_star_image", { defaultValue: "Star image" })}
                >
                  <i className={viewerStarredImage ? "ti ti-star-filled" : "ti ti-star"} />
                </button>
              ) : null}
              {viewerImageContext && isLineDeliveryAvailable ? (
                <button
                  className="restaurant-chatbot-image-viewer-action"
                  type={BUTTON_TYPE}
                  onClick={() => {
                    if (!openedImage) return;
                    void sendImageToLineWithConfirmation({
                      sessionId: viewerImageContext.sessionId,
                      turnId: viewerImageContext.turnId,
                      imageUrl: openedImage.src,
                      title: viewerImageContext.title || openedImage.alt,
                    });
                  }}
                  aria-label={t("chatbot_send_to_line", { defaultValue: "Send to LINE" })}
                  data-tooltip={t("chatbot_send_to_line", { defaultValue: "Send to LINE" })}
                >
                  <img className="restaurant-chatbot-line-icon" src={LINE_APP_ICON_SRC} alt="LINE" />
                </button>
              ) : null}
              <button
                className="restaurant-chatbot-image-viewer-action"
                type={BUTTON_TYPE}
                onClick={handleViewerClose}
                aria-label="Close image viewer"
                data-tooltip="Close"
              >
                <i className={CLOSE_ICON_CLASS_NAME} />
              </button>
            </div>
            <div className="restaurant-chatbot-image-viewer-inner">
              <img
                src={openedImage.src}
                alt={openedImage.alt || openedImage.filename}
                onError={handleViewerClose}
              />
            </div>
          </div>
        </div>
      )}
      {showTeaser && !isOpen && (
        <aside className="restaurant-chatbot-teaser" aria-live="polite">
          <button
            className="restaurant-chatbot-teaser-close"
            type={BUTTON_TYPE}
            onClick={dismissTeaser}
            title={t(DISMISS_TEASER_TRANSLATION_KEY)}
          >
            <i className={CLOSE_ICON_CLASS_NAME} />
          </button>
          <p>{t(TEASER_MESSAGE_TRANSLATION_KEY)}</p>
        </aside>
      )}

      <button
        className={`restaurant-chatbot-floating-button${isOpen ? OPEN_CLASS_NAME : ""}`}
        type={BUTTON_TYPE}
        onClick={handleToggleChat}
        title={isOpen ? t(CLOSE_CHAT_TRANSLATION_KEY) : t(OPEN_CHAT_TRANSLATION_KEY)}
      >
        <i className={`${OPEN_ICON_CLASS_NAME} restaurant-chatbot-launcher-icon open-icon`} />
        <i className={`${CLOSE_ICON_CLASS_NAME} restaurant-chatbot-launcher-icon close-icon`} />
      </button>
    </RestaurantChatbotShell>
  );
};

export default RestaurantChatbotWidget;
